/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import Link from 'next/link';
import styles from './informativeBanner.module.scss';
import trackHeapEvent from '../../services/helpers/trackHeapEvent';

// Exposes the banner's rendered height so the fixed header stack
// (gradient bar + header + content spacer) can shift down to sit beneath it.
// Defaults to 0 on pages without the banner, so other layouts are unaffected.
const BANNER_HEIGHT_VAR = '--hpn-informative-banner-height';

function InformativeBanner({
  description = 'Interested in a Savings or Checking Account?',
  cta = { label: 'Learn More', link: '/' },
  icon = {},
  displayType = 'fixed',
  theme = 'dark',
}) {
  const [display, setDisplay] = useState(true);
  const bannerRef = useRef(null);
  const typeClassName = displayType === 'fixed' ? styles.fixed : styles.sticky;
  const themeClassName = styles[theme] ?? '';

  // Keep the header offset variable in sync with the banner's actual height
  // (single line on desktop, multi-line on mobile) and clear it on dismiss.
  useEffect(() => {
    const root = document.documentElement;
    if (!display) {
      root.style.setProperty(BANNER_HEIGHT_VAR, '0px');
      return undefined;
    }

    const node = bannerRef.current;
    if (!node) return undefined;

    const setHeight = () => {
      const { height } = node.getBoundingClientRect();
      root.style.setProperty(BANNER_HEIGHT_VAR, `${Math.floor(height)}px`);
    };
    setHeight();

    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(setHeight)
        : null;
    observer?.observe(node);
    window.addEventListener('resize', setHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', setHeight);
      root.style.setProperty(BANNER_HEIGHT_VAR, '0px');
    };
  }, [display]);

  const handleClose = () => {
    setDisplay(false);
  };

  const BannerHeapTrack = (ev) => {
    ev.preventDefault();
    trackHeapEvent(
      {
        Name: 'Infomative Banner',
        PageTitle: document.title,
      },
      'CMS_Unification'
    );
    window.location.assign(cta.link);
  };

  return (
    display && (
      <div
        ref={bannerRef}
        className={`${styles.informativeBanner} ${typeClassName} ${themeClassName}`}
      >
        <div className={styles.content}>
          <div className={styles.items}>
            {icon && icon.url && (
              <img
                className={styles.icon}
                src={icon.url}
                alt={icon.alt}
                loading="lazy"
                {...{
                  ...(icon.width && {
                    width: icon.width,
                  }),
                  ...(icon.height && {
                    height: icon.height,
                  }),
                }}
              />
            )}
            <span className={styles.text}>{parse(`${description}`)}</span>
            {cta && (
              <Link
                className={styles.cta}
                href={cta.link}
                onClick={(e) => BannerHeapTrack(e)}
              >
                {cta.label}
              </Link>
            )}
          </div>
          <button
            type="button"
            className={styles.close}
            data-testid="close-icon"
            aria-label="Close"
            onClick={handleClose}
          />
        </div>
      </div>
    )
  );
}

InformativeBanner.propTypes = {
  description: PropTypes.string,
  icon: PropTypes.shape({
    url: PropTypes.string,
    alt: PropTypes.string,
  }),
  cta: PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
  }),
  displayType: PropTypes.string,
  theme: PropTypes.oneOf(['dark', 'primaryBlue']),
};

export default InformativeBanner;
